import { ModalTypes, Typography15, useModal } from "@slid/slid-ips";
import env from "config/env";
import { useAppSelector } from "hooks";
import { useStudyChallengeNote, useStudyChallengeStatus, useUserStudyChallenge } from "hooks/queries";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { eventTypes } from "types/eventTracking";
import { trackEvent } from "utils/eventTracking";
import { sendMessageToPrimary } from "utils/utils";

const SLID_WEB_APP_URL = env.end_point_url.slid_web_app;

const VideoNoteStudyChallengeInfo = () => {
  const { isUserParticipated, userStudyGoal } = useUserStudyChallenge();
  const { isNoteSubmitted, submitStudyChallengeNote, reSubmitStudyChallengeNote } = useStudyChallengeNote();
  const { StudyChallengeStatus } = useStudyChallengeStatus();

  const { applicationType } = useAppSelector((state) => state.slidGlobal);
  const { iframePort } = useAppSelector((state) => state.vdocs);

  const { showModal, closeModal } = useModal();

  const [isHover, setIsHover] = React.useState(false);

  const history = useHistory();

  const { t } = useTranslation("StudyChallenge");

  if (!isUserParticipated) return <></>;

  return (
    <Wrapper>
      <VideoNoteStudyChallengeInfoWrapper
        onMouseEnter={() => {
          setIsHover(true);
        }}
        onMouseLeave={() => {
          setIsHover(false);
        }}
      >
        <LeftContainer>
          <DayInfo>
            <Typography15
              weight={700}
              color="--blue8"
              text={t("GroupChallenge.Submit.Ongoing", { keySeparator: ".", currentDay: StudyChallengeStatus?.today_period, totalDays: StudyChallengeStatus?.total_periods })}
              type={"span"}
            />
          </DayInfo>
          <Typography15 weight={500} color="--gray15" text={userStudyGoal} type={"span"} />
        </LeftContainer>
        <RightContainer>
          <Typography15
            weight={700}
            color="--gray15"
            text={t("GroupChallenge.Submit.SubmitNote", { keySeparator: "." })}
            type={"span"}
            style={
              isHover
                ? {
                    textDecoration: "underline",
                  }
                : {}
            }
            onClick={async () => {
              trackEvent({
                eventType: eventTypes.click.SUBMIT_GROUP_CHALLENGE_NOTE,
                eventProperties: {
                  location: "Video note page",
                },
              });

              if (isNoteSubmitted) {
                trackEvent({
                  eventType: eventTypes.show.GROUP_CHALLENGE_NOTE_SUBMIT_MODAL,
                  eventProperties: {
                    status: "failed",
                    from: "Video note page",
                  },
                });

                showModal({
                  type: ModalTypes.emojiModal,
                  device: "desktop",
                  title: t("GroupChallenge.Submit.AlreadySubmittedNote", { keySeparator: "." }),
                  emoji: "🤔",
                  text: t("GroupChallenge.Submit.ResubmitNote", { keySeparator: "." }),
                  primaryButton: {
                    callBack: async () => {
                      trackEvent({
                        eventType: eventTypes.click.SUBMIT_IN_GROUP_CHALLENGE_NOTE_SUBMIT_MODAL,
                      });

                      await reSubmitStudyChallengeNote();

                      if (applicationType === "extension") {
                        window.open("/docs?section=study-challenge");
                      } else {
                        if (applicationType === "desktop") {
                          sendMessageToPrimary({
                            message: {
                              type: "IFRAME_TO_PRIMARY_OPEN_URL",
                              payload: `${SLID_WEB_APP_URL}/docs?section=study-challenge`,
                            },
                            port: iframePort,
                          });

                          closeModal();

                          return;
                        }

                        history.push("/docs?section=study-challenge");
                      }
                    },
                    text: t("GroupChallenge.Submit.Resubmit", { keySeparator: "." }),
                  },
                  secondaryButton: {
                    callBack: () => {
                      trackEvent({
                        eventType: eventTypes.click.CANCEL_IN_GROUP_CHALLENGE_NOTE_SUBMIT_MODAL,
                      });

                      closeModal();
                    },
                    text: t("GroupChallenge.Submit.Cancel", { keySeparator: "." }),
                  },
                });
              } else {
                await submitStudyChallengeNote();

                trackEvent({
                  eventType: eventTypes.show.GROUP_CHALLENGE_NOTE_SUBMIT_MODAL,
                  eventProperties: {
                    status: "success",
                    from: "Video note page",
                  },
                });

                showModal({
                  type: ModalTypes.emojiModal,
                  device: "desktop",
                  title: t("GroupChallenge.Submit.SubmittedNote", { keySeparator: "." }),
                  emoji: "🥳",
                  text: t("GroupChallenge.Submit.SubmittedNoteDesc", { keySeparator: ".", currentDay: StudyChallengeStatus?.today_period, totalDays: StudyChallengeStatus?.total_periods }),
                  primaryButton: {
                    callBack: () => {
                      trackEvent({
                        eventType: eventTypes.click.CHECK_SUBMITTED_NOTE_IN_GROUP_CHALLENGE_NOTE_SUBMIT_MODAL,
                      });

                      if (applicationType === "extension") {
                        window.open("/docs?section=study-challenge");
                      } else {
                        if (applicationType === "desktop") {
                          sendMessageToPrimary({
                            message: {
                              type: "IFRAME_TO_PRIMARY_OPEN_URL",
                              payload: `${SLID_WEB_APP_URL}/docs?section=study-challenge`,
                            },
                            port: iframePort,
                          });

                          closeModal();

                          return;
                        }

                        history.push("/docs?section=study-challenge");
                      }
                    },
                    text: t("GroupChallenge.Submit.ViewSubmittedNote", { keySeparator: "." }),
                  },
                  secondaryButton: {
                    callBack: () => {
                      trackEvent({
                        eventType: eventTypes.click.NOTE_TACKING_IN_GROUP_CHALLENGE_NOTE_SUBMIT_MODAL,
                      });

                      closeModal();
                    },
                    text: t("GroupChallenge.Submit.ContinueNote", { keySeparator: "." }),
                  },
                });
              }
            }}
          />
        </RightContainer>
      </VideoNoteStudyChallengeInfoWrapper>
    </Wrapper>
  );
};

export default VideoNoteStudyChallengeInfo;

const Wrapper = styled.div`
  padding-top: 8px;
  padding-left: 18px;
  padding-right: 18px;
`;

const VideoNoteStudyChallengeInfoWrapper = styled.div`
  margin: auto;
  width: 100%;
  padding: 7px 16px;
  border-radius: 4px;
  background: var(--gray2);

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RightContainer = styled.div`
  cursor: pointer;
`;

const DayInfo = styled.div`
  padding: 4px 12px;
  border-radius: 100px;
  border: 1px solid var(--blue5);
  background: var(--white);
`;
